import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../stores/authStore';
import { usePermissions } from '../../hooks/usePermissions';
import PageHelp from '../../components/PageHelp';

interface ProfitabilityReportData {
  summary: {
    totalRevenue: string;
    totalTaxCollected: string;
    totalInvoices: number;
    totalItems: number;
    totalCustomers: number;
    averageInvoiceValue: string;
  };
  itemWise: Array<{
    item: string;
    quantity: string;
    revenue: string;
    averagePrice: string;
    taxCollected: string;
    invoiceCount: number;
    transactions: Array<{
      date: string;
      invoiceNumber: string;
      customer: string;
      quantity: string;
      unitPrice: string;
      revenue: string;
      taxAmount: string;
    }>;
  }>;
  customerWise: Array<{
    customerId: string;
    customerName: string;
    revenue: string;
    taxCollected: string;
    invoiceCount: number;
    averageInvoiceValue: string;
    largestInvoice: string;
    smallestInvoice: string;
    transactions: Array<{
      date: string;
      invoiceNumber: string;
      revenue: string;
      taxAmount: string;
      status: string;
    }>;
  }>;
}

const ProfitabilityReports: React.FC = () => {
  const { user } = useAuthStore();
  const { hasPermission, loading: permissionsLoading } = usePermissions(user);
  const [reportData, setReportData] = useState<ProfitabilityReportData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [reportType, setReportType] = useState<'comprehensive' | 'item-wise' | 'customer-wise'>('comprehensive');
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    accountId: '',
    costCenterId: '',
  });
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [expandedCustomer, setExpandedCustomer] = useState<string | null>(null);

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return '£0.00';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(num);
  };

  const formatNumber = (num: string | number) => {
    const n = typeof num === 'string' ? parseFloat(num) : num;
    if (isNaN(n)) return '0';
    return new Intl.NumberFormat('en-GB', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  };

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      let endpoint = '/api/erp/profitability-reports/generate';
      if (reportType === 'item-wise') {
        endpoint = '/api/erp/profitability-reports/item-wise';
      } else if (reportType === 'customer-wise') {
        endpoint = '/api/erp/profitability-reports/customer-wise';
      }

      const response = await axios.post(endpoint, filters);
      if (response.data.success) {
        if (reportType === 'comprehensive') {
          setReportData(response.data.data);
        } else if (reportType === 'item-wise') {
          setReportData({
            summary: {
              totalRevenue: '0',
              totalTaxCollected: '0',
              totalInvoices: 0,
              totalItems: response.data.data.length,
              totalCustomers: 0,
              averageInvoiceValue: '0',
            },
            itemWise: response.data.data,
            customerWise: [],
          });
        } else if (reportType === 'customer-wise') {
          setReportData({
            summary: {
              totalRevenue: '0',
              totalTaxCollected: '0',
              totalInvoices: 0,
              totalItems: 0,
              totalCustomers: response.data.data.length,
              averageInvoiceValue: '0',
            },
            itemWise: [],
            customerWise: response.data.data,
          });
        }
        toast.success('Profitability report generated successfully');
      } else {
        toast.error(response.data.error || 'Failed to generate profitability report');
      }
    } catch (error: any) {
      console.error('Error generating profitability report:', error);
      toast.error(error.response?.data?.error || 'Failed to generate profitability report');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportCSV = async (type: 'item-wise' | 'customer-wise') => {
    try {
      let endpoint = '/api/erp/profitability-reports/item-wise/export-csv';
      if (type === 'customer-wise') {
        endpoint = '/api/erp/profitability-reports/customer-wise/export-csv';
      }

      const response = await axios.post(
        endpoint,
        filters,
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}-profitability-${filters.startDate}-${filters.endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Report exported successfully');
    } catch (error: any) {
      console.error('Error exporting report:', error);
      toast.error(error.response?.data?.error || 'Failed to export report');
    }
  };

  if (permissionsLoading) {
    return <div className="p-6">Loading...</div>;
  }

  if (!hasPermission('erp_financial_report', 'read')) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">You do not have permission to view profitability reports.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center">
          <h1 className="text-2xl font-bold text-gray-900">Profitability Reports</h1>
          <PageHelp articleKey="erp_profitability" />
        </div>
        <p className="text-gray-600 mt-1">Analyze revenue by item and customer</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <form onSubmit={handleGenerate}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Report Type
              </label>
              <select
                value={reportType}
                onChange={(e) => setReportType(e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="comprehensive">Comprehensive</option>
                <option value="item-wise">Item-Wise Only</option>
                <option value="customer-wise">Customer-Wise Only</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div className="flex items-end">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Generating...' : 'Generate Report'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Summary */}
      {reportData && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Summary</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div>
              <p className="text-sm text-gray-600">Total Revenue</p>
              <p className="text-lg font-semibold text-gray-900">
                {formatCurrency(reportData.summary.totalRevenue)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Tax</p>
              <p className="text-lg font-semibold text-gray-900">
                {formatCurrency(reportData.summary.totalTaxCollected)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Invoices</p>
              <p className="text-lg font-semibold text-gray-900">
                {reportData.summary.totalInvoices}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Items</p>
              <p className="text-lg font-semibold text-gray-900">
                {reportData.summary.totalItems}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Customers</p>
              <p className="text-lg font-semibold text-gray-900">
                {reportData.summary.totalCustomers}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Avg Invoice Value</p>
              <p className="text-lg font-semibold text-gray-900">
                {formatCurrency(reportData.summary.averageInvoiceValue)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Item-Wise Report */}
      {reportData && reportData.itemWise.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Item-Wise Profitability</h2>
            <button
              onClick={() => handleExportCSV('item-wise')}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              Export CSV
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Item
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Revenue
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Avg Price
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tax
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoices
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reportData.itemWise.map((item, index) => (
                  <React.Fragment key={index}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{item.item}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">
                        {formatNumber(item.quantity)}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">
                        {formatCurrency(item.revenue)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">
                        {formatCurrency(item.averagePrice)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">
                        {formatCurrency(item.taxCollected)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">
                        {item.invoiceCount}
                      </td>
                      <td className="px-4 py-3 text-sm text-center">
                        <button
                          onClick={() => setExpandedItem(expandedItem === item.item ? null : item.item)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          {expandedItem === item.item ? 'Hide' : 'View'} Details
                        </button>
                      </td>
                    </tr>
                    {expandedItem === item.item && item.transactions.length > 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-3 bg-gray-50">
                          <div className="overflow-x-auto">
                            <table className="min-w-full">
                              <thead>
                                <tr>
                                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500">Date</th>
                                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500">Invoice</th>
                                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500">Customer</th>
                                  <th className="px-2 py-1 text-right text-xs font-medium text-gray-500">Qty</th>
                                  <th className="px-2 py-1 text-right text-xs font-medium text-gray-500">Unit Price</th>
                                  <th className="px-2 py-1 text-right text-xs font-medium text-gray-500">Revenue</th>
                                  <th className="px-2 py-1 text-right text-xs font-medium text-gray-500">Tax</th>
                                </tr>
                              </thead>
                              <tbody>
                                {item.transactions.map((trans, tIndex) => (
                                  <tr key={tIndex} className="border-t">
                                    <td className="px-2 py-1 text-xs text-gray-600">
                                      {new Date(trans.date).toLocaleDateString()}
                                    </td>
                                    <td className="px-2 py-1 text-xs text-gray-600">{trans.invoiceNumber}</td>
                                    <td className="px-2 py-1 text-xs text-gray-600">{trans.customer}</td>
                                    <td className="px-2 py-1 text-xs text-gray-600 text-right">
                                      {formatNumber(trans.quantity)}
                                    </td>
                                    <td className="px-2 py-1 text-xs text-gray-600 text-right">
                                      {formatCurrency(trans.unitPrice)}
                                    </td>
                                    <td className="px-2 py-1 text-xs text-gray-600 text-right">
                                      {formatCurrency(trans.revenue)}
                                    </td>
                                    <td className="px-2 py-1 text-xs text-gray-600 text-right">
                                      {formatCurrency(trans.taxAmount)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Customer-Wise Report */}
      {reportData && reportData.customerWise.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Customer-Wise Profitability</h2>
            <button
              onClick={() => handleExportCSV('customer-wise')}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              Export CSV
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Revenue
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tax
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoices
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Avg Invoice
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Largest
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Smallest
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reportData.customerWise.map((customer, index) => (
                  <React.Fragment key={customer.customerId}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{customer.customerName}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">
                        {formatCurrency(customer.revenue)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">
                        {formatCurrency(customer.taxCollected)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">
                        {customer.invoiceCount}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">
                        {formatCurrency(customer.averageInvoiceValue)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">
                        {formatCurrency(customer.largestInvoice)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">
                        {formatCurrency(customer.smallestInvoice)}
                      </td>
                      <td className="px-4 py-3 text-sm text-center">
                        <button
                          onClick={() => setExpandedCustomer(expandedCustomer === customer.customerId ? null : customer.customerId)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          {expandedCustomer === customer.customerId ? 'Hide' : 'View'} Details
                        </button>
                      </td>
                    </tr>
                    {expandedCustomer === customer.customerId && customer.transactions.length > 0 && (
                      <tr>
                        <td colSpan={8} className="px-4 py-3 bg-gray-50">
                          <div className="overflow-x-auto">
                            <table className="min-w-full">
                              <thead>
                                <tr>
                                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500">Date</th>
                                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500">Invoice</th>
                                  <th className="px-2 py-1 text-right text-xs font-medium text-gray-500">Revenue</th>
                                  <th className="px-2 py-1 text-right text-xs font-medium text-gray-500">Tax</th>
                                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {customer.transactions.map((trans, tIndex) => (
                                  <tr key={tIndex} className="border-t">
                                    <td className="px-2 py-1 text-xs text-gray-600">
                                      {new Date(trans.date).toLocaleDateString()}
                                    </td>
                                    <td className="px-2 py-1 text-xs text-gray-600">{trans.invoiceNumber}</td>
                                    <td className="px-2 py-1 text-xs text-gray-600 text-right">
                                      {formatCurrency(trans.revenue)}
                                    </td>
                                    <td className="px-2 py-1 text-xs text-gray-600 text-right">
                                      {formatCurrency(trans.taxAmount)}
                                    </td>
                                    <td className="px-2 py-1 text-xs text-gray-600">{trans.status}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {reportData && reportData.itemWise.length === 0 && reportData.customerWise.length === 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-600 text-center">No data found for the selected period.</p>
        </div>
      )}
    </div>
  );
};

export default ProfitabilityReports;
